import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert
} from "@mui/material";
import {
    Edit,
    Visibility,
    CheckCircle,
    Warning,
    FilterList,
    Refresh
} from "@mui/icons-material";
import Schedule from "@mui/icons-material/Schedule";
import Assignment from "@mui/icons-material/Assignment";
import MoreVert from "@mui/icons-material/MoreVert";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import TablePagination from "@mui/material/TablePagination";
import Clear from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import { formatCurrency } from "../capital/capitalUtils";

const AdminOpportunitiesTable = ({ onOpportunitySelect, onRefresh, showFilters = true, maxHeight = 600 }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [opportunities, setOpportunities] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState({ status: "all", riskLevel: "all", assignedTo: "all", search: "" });
    const [assignmentDialog, setAssignmentDialog] = useState({ open: false, opportunity: null, selectedAgent: "" });
    const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: "", opportunities: [] });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");
            const [oppsData] = await Promise.all([
                opportunitiesService.getActiveOpportunities()
                // TODO(api): Replace direct agentService import with API call
                // agentService.listAgents()
            ]);
            setOpportunities(oppsData);
            // setAgents(agentsData || []); // Uncomment when agentService is used
        } catch (error) {
            setError("Error al cargar los datos: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredOpportunities = (opportunities || []).filter(opp => {
        if (filters.status !== "all" && opp.status !== filters.status) return false;
        if (filters.riskLevel !== "all" && opp.riskLevel !== filters.riskLevel) return false;
        if (filters.assignedTo !== "all") {
            if (filters.assignedTo === "unassigned" && opp.agentId) return false;
            if (filters.assignedTo !== "unassigned" && opp.agentId !== filters.assignedTo) return false;
        }
        if (filters.search && !opp.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
        return true;
    });

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(opp => opp.id);
            setSelected(newSelected);
        } else { setSelected([]); }
    };
    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) { newSelected = newSelected.concat(selected, id); }
        else if (selectedIndex === 0) { newSelected = newSelected.concat(selected.slice(1)); }
        else if (selectedIndex === selected.length - 1) { newSelected = newSelected.concat(selected.slice(0, -1)); }
        else if (selectedIndex > 0) { newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1)); }
        setSelected(newSelected);
    };
    const isSelected = (id) => selected.indexOf(id) !== -1;
    const handleFilterChange = (field, value) => { setFilters(prev => ({ ...prev, [field]: value })); setPage(0); };
    const clearFilters = () => { setFilters({ status: "all", riskLevel: "all", assignedTo: "all", search: "" }); setPage(0); };
    const handleViewOpportunity = (opportunity) => { if (onOpportunitySelect) { onOpportunitySelect(opportunity); } else { navigate(`/opportunity/${opportunity.id}`); } };
    const handleAssignAgent = (opportunity) => { setAssignmentDialog({ open: true, opportunity, selectedAgent: opportunity.agentId || "" }); };
    const confirmAssignment = async () => {
        try {
            const { opportunity, selectedAgent } = assignmentDialog;
            await opportunitiesService.updateOpportunity(opportunity.id, { agentId: selectedAgent, assignedAt: new Date().toISOString() });
            setAssignmentDialog({ open: false, opportunity: null, selectedAgent: "" });
            loadData();
            if (onRefresh) onRefresh();
        } catch (error) { setError("Error al asignar agente: " + error.message); }
    };
    const handleBulkAction = (action) => {
        const selectedOpportunities = (opportunities || []).filter(opp => selected.includes(opp.id));
        setBulkActionDialog({ open: true, action, opportunities: selectedOpportunities });
    };
    const confirmBulkAction = async () => {
        try {
            const { action, opportunities: selectedOpps } = bulkActionDialog;
            switch (action) {
            case "delete": await Promise.all(selectedOpps.map(opp => opportunitiesService.deleteOpportunity(opp.id))); break;
            case "activate": await Promise.all(selectedOpps.map(opp => opportunitiesService.updateOpportunity(opp.id, { status: "active" }))); break;
            case "deactivate": await Promise.all(selectedOpps.map(opp => opportunitiesService.updateOpportunity(opp.id, { status: "inactive" }))); break;
            }
            setBulkActionDialog({ open: false, action: "", opportunities: [] });
            setSelected([]);
            loadData();
            if (onRefresh) onRefresh();
        } catch (error) { setError("Error al ejecutar acción masiva: " + error.message); }
    };
    const getStatusColor = (status) => {
        switch (status) {
        case "active": return "success";
        case "pending": return "warning";
        case "draft": return "default";
        case "closed": return "error";
        case "inactive": return "error";
        default: return "default";
        }
    };
    const getRiskColor = (riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
        case "low": return "success";
        case "medium": return "warning";
        case "high": return "error";
        default: return "default";
        }
    };
    const getAssignedAgentName = (agentId) => {
        const agent = agents.find(a => a.id === agentId);
        return agent ? agent.nombre : "Sin asignar";
    };
    const getCompletionStatus = (opportunity) => {
        let completion = 0; let factors = 0;
        if (opportunity.title) { completion += 20; factors++; }
        if (opportunity.description) { completion += 20; factors++; }
        if (opportunity.amount) { completion += 20; factors++; }
        if (opportunity.agentId) { completion += 20; factors++; }
        if (opportunity.status === "active") { completion += 20; factors++; }
        return factors > 0 ? Math.round(completion / factors) : 0;
    };
    const getStatusIcon = (status) => {
        switch (status) {
        case "active": return <CheckCircle color="success" />;
        case "pending": return <Schedule color="warning" />;
        case "draft": return <Edit color="default" />;
        case "closed": return <Warning color="error" />;
        default: return <Schedule color="default" />;
        }
    };
    if (loading) {
        return (
            <Box sx={{ width: "100%" }}>
                <LinearProgress />
                <Typography sx={{ mt: 2, textAlign: "center" }}>Cargando oportunidades...</Typography>
            </Box>
        );
    }
    return (
        <Box>
            {error && (<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>)}
            {showFilters && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <FilterList /> Filtros Avanzados
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField fullWidth label="Buscar por título" value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)} size="small" />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Estado</InputLabel>
                                    <Select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="draft">Borrador</MenuItem>
                                        <MenuItem value="pending">Pendiente</MenuItem>
                                        <MenuItem value="active">Activo</MenuItem>
                                        <MenuItem value="inactive">Inactivo</MenuItem>
                                        <MenuItem value="closed">Cerrado</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Riesgo</InputLabel>
                                    <Select value={filters.riskLevel} onChange={(e) => handleFilterChange("riskLevel", e.target.value)}>
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="low">Bajo</MenuItem>
                                        <MenuItem value="medium">Medio</MenuItem>
                                        <MenuItem value="high">Alto</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Asignado</InputLabel>
                                    <Select value={filters.assignedTo} onChange={(e) => handleFilterChange("assignedTo", e.target.value)}>
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="unassigned">Sin asignar</MenuItem>
                                        {agents.map(agent => (<MenuItem key={agent.id} value={agent.id}>{agent.nombre}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="outlined" startIcon={<Clear />} onClick={clearFilters} size="small">Limpiar</Button>
                                    <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} size="small">Actualizar</Button>
                                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>{filteredOpportunities.length} de {(opportunities || []).length}</Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}
            {selected.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1}>
                        <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>{selected.length} seleccionado(s)</Typography>
                        <Button size="small" variant="outlined" onClick={() => handleBulkAction("activate")}>Activar</Button>
                        <Button size="small" variant="outlined" onClick={() => handleBulkAction("deactivate")}>Desactivar</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleBulkAction("delete")}>Eliminar</Button>
                    </Stack>
                </Box>
            )}
            <TableContainer component={Paper} sx={{ maxHeight }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox indeterminate={selected.length > 0 && selected.length < filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length} checked={filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 && selected.length === filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length} onChange={handleSelectAllClick} />
                            </TableCell>
                            <TableCell>Oportunidad</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Riesgo</TableCell>
                            <TableCell>Monto</TableCell>
                            <TableCell>Asignado</TableCell>
                            <TableCell>Progreso</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((opportunity) => {
                            const isItemSelected = isSelected(opportunity.id);
                            const completion = getCompletionStatus(opportunity);
                            return (
                                <TableRow hover key={opportunity.id} selected={isItemSelected} onClick={(event) => handleClick(event, opportunity.id)} sx={{ cursor: "pointer" }}>
                                    <TableCell padding="checkbox"><Checkbox checked={isItemSelected} /></TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>{opportunity.title}</Typography>
                                            <Typography variant="caption" color="textSecondary">{opportunity.location || "Ubicación no especificada"}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {getStatusIcon(opportunity.status)}
                                            <Chip label={opportunity.status} color={getStatusColor(opportunity.status)} size="small" />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={opportunity.riskLevel || "N/A"} color={getRiskColor(opportunity.riskLevel)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>{formatCurrency(opportunity.amount || 0, "EUR")}</Typography>
                                            <Typography variant="caption" color="textSecondary">Mín: {formatCurrency(opportunity.minimumInvestment || 0, "EUR")}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{getAssignedAgentName(opportunity.agentId)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box sx={{ width: 60, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
                                                <Box sx={{ width: `${completion}%`, height: 8, bgcolor: completion >= 80 ? "success.main" : completion >= 50 ? "warning.main" : "error.main" }} />
                                            </Box>
                                            <Typography variant="caption">{completion}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{new Date(opportunity.createdAt).toLocaleDateString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Ver detalles">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewOpportunity(opportunity); }}><Visibility /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Asignar agente">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAssignAgent(opportunity); }}><Assignment /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Más acciones">
                                                <IconButton size="small"><MoreVert /></IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredOpportunities.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página:" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
            <Dialog open={assignmentDialog.open} onClose={() => setAssignmentDialog({ open: false, opportunity: null, selectedAgent: "" })} maxWidth="sm" fullWidth>
                <DialogTitle>Asignar Agente a Oportunidad</DialogTitle>
                <DialogContent>
                    {assignmentDialog.opportunity && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>{assignmentDialog.opportunity.title}</Typography>
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Seleccionar Agente</InputLabel>
                                <Select value={assignmentDialog.selectedAgent} onChange={(e) => setAssignmentDialog(prev => ({ ...prev, selectedAgent: e.target.value }))}>
                                    <MenuItem value="">Sin asignar</MenuItem>
                                    {agents.map(agent => (<MenuItem key={agent.id} value={agent.id}>{agent.nombre} ({agent.tipo || "Agente"})</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignmentDialog({ open: false, opportunity: null, selectedAgent: "" })}>Cancelar</Button>
                    <Button onClick={confirmAssignment} variant="contained">Confirmar Asignación</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={bulkActionDialog.open} onClose={() => setBulkActionDialog({ open: false, action: "", opportunities: [] })} maxWidth="sm" fullWidth>
                <DialogTitle>Confirmar Acción Masiva</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>¿Estás seguro de que quieres {bulkActionDialog.action === "delete" ? "eliminar" : bulkActionDialog.action === "activate" ? "activar" : "desactivar"} {bulkActionDialog.opportunities.length} oportunidad(es)?</Typography>
                    <Box sx={{ mt: 2 }}>
                        {bulkActionDialog.opportunities.slice(0, 5).map(opp => (<Typography key={opp.id} variant="body2" color="textSecondary">• {opp.title}</Typography>))}
                        {bulkActionDialog.opportunities.length > 5 && (<Typography variant="body2" color="textSecondary">... y {bulkActionDialog.opportunities.length - 5} más</Typography>)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkActionDialog({ open: false, action: "", opportunities: [] })}>Cancelar</Button>
                    <Button onClick={confirmBulkAction} variant="contained" color={bulkActionDialog.action === "delete" ? "error" : "primary"}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminOpportunitiesTable;
